/**
 * Popover action list anchored to an IconButton or Button. Supports dividers
 * and destructive items.
 * Spec: assets/handoffs/design_system_export/specs/DropdownMenu.json
 *
 * The panel renders into a document.body portal with `position: fixed`,
 * positioned from the trigger's getBoundingClientRect() and flipped to open
 * upward when there isn't room below it in the viewport. This is required
 * (not just nicer) because triggers commonly live inside an
 * `overflow: hidden` Card — a plain absolutely-positioned panel would get
 * clipped by the card boundary even when the *viewport* has room, e.g. a
 * one-row table near the bottom of the page. No horizontal flip / full
 * collision library; scoped to the vertical case that comes up in practice.
 *
 * @param {ReactElement} trigger - a Button/IconButton element; gets aria-haspopup/aria-expanded and a click handler cloned onto it
 * @param {Array<{key?:string,type?:'divider',label?:string,icon?:ReactNode,disabled?:boolean,destructive?:boolean,onClick?:function}>} items
 * @param {'start'|'end'=end} align
 * @param {string=} panelClassName - extra class merged onto the portaled panel, for a caller that needs a resolved width/etc (the panel isn't reachable from `className`, which only reaches the trigger wrapper)
 * @param {ReactNode=} footer - optional content rendered below the items, visually separated by a top rule
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import styles from "./DropdownMenu.module.scss";

const OFFSET = 4;

const DropdownMenu = ({ trigger, items = [], align = "end", className, panelClassName, footer, ...rest }) => {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const itemRefs = useRef([]);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const close = (returnFocus = true) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;

    const handleClick = (e) => {
      if (
        triggerRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const firstEnabled = items.findIndex((item) => item.type !== "divider" && !item.disabled);
    itemRefs.current[firstEnabled]?.focus();
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !panelRef.current) return;

    const reposition = () => {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const panelHeight = panelRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const fitsBelow = spaceBelow >= panelHeight + OFFSET;
      const fitsAbove = spaceAbove >= panelHeight + OFFSET;
      // Prefer below (the common case); flip up if below doesn't fit and
      // either above does, or above is just the roomier side we've got.
      const openUpward = !fitsBelow && (fitsAbove || spaceAbove > spaceBelow);

      setPanelStyle({
        position: "fixed",
        ...(align === "end"
          ? { right: window.innerWidth - triggerRect.right }
          : { left: triggerRect.left }),
        ...(openUpward
          ? { bottom: window.innerHeight - triggerRect.top + OFFSET }
          : { top: triggerRect.bottom + OFFSET }),
      });
    };

    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, align, items]);

  const enabledIndexes = items
    .map((item, i) => (item.type !== "divider" && !item.disabled ? i : null))
    .filter((i) => i !== null);

  const handleKeyDown = (e, index) => {
    const pos = enabledIndexes.indexOf(index);
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      itemRefs.current[enabledIndexes[(pos + 1) % enabledIndexes.length]]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      itemRefs.current[enabledIndexes[(pos - 1 + enabledIndexes.length) % enabledIndexes.length]]?.focus();
    } else if (e.key === "Tab") {
      close(false);
    }
  };

  return (
    <div className={classNames(styles.root, className)} {...rest}>
      {React.cloneElement(trigger, {
        ref: triggerRef,
        onClick: (e) => {
          trigger.props.onClick?.(e);
          setOpen((o) => !o);
        },
        "aria-haspopup": "menu",
        "aria-expanded": open,
      })}
      {open &&
        createPortal(
          <div
            role="menu"
            ref={panelRef}
            className={classNames(styles.panel, panelClassName)}
            // Positioned imperatively (fixed, computed from the trigger's
            // rect) rather than via the panel--start/panel--end/panel--up
            // CSS variants — see the file-level comment for why.
            style={panelStyle || { position: "fixed", top: -9999, left: -9999 }}
          >
            {items.map((item, index) =>
              item.type === "divider" ? (
                <div key={`divider-${index}`} role="separator" className={styles.divider} />
              ) : (
                <button
                  key={item.key || index}
                  ref={(el) => (itemRefs.current[index] = el)}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  className={classNames(styles.item, {
                    [styles["item--destructive"]]: item.destructive,
                  })}
                  onClick={() => {
                    if (item.disabled) return;
                    item.onClick?.();
                    close();
                  }}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                >
                  {item.icon && (
                    <span className={styles.itemIcon} aria-hidden="true">
                      {item.icon}
                    </span>
                  )}
                  {item.label}
                </button>
              )
            )}
            {footer && <div className={styles.footer}>{footer}</div>}
          </div>,
          document.body
        )}
    </div>
  );
};

export default DropdownMenu;
